import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Droplets, FlaskConical, AlertTriangle, CheckCircle, Info, ShoppingCart, Loader2, Camera } from 'lucide-react';
import { apiRequest, getStatusColor, getStatusLabel, formatPrice } from '../lib/utils';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ShareAnalysisButton from '../components/ShareAnalysisButton';
import SEOHead, { PAGE_SEO } from '../components/SEOHead';

export default function AnalysisPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { addToCart } = useCart();
    
    const [values, setValues] = useState({
        ph: 7.4,
        chlorine: 2.0,
        calcium: 300,
        stabilizer: 40,
        pool_volume: 50000
    });
    
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);

    const ranges = {
        ph: { min: 0, max: 14, step: 0.1, unit: '', idealMin: 7.2, idealMax: 7.6 },
        chlorine: { min: 0, max: 10, step: 0.1, unit: 'ppm', idealMin: 1.0, idealMax: 3.0 },
        calcium: { min: 0, max: 1000, step: 10, unit: 'ppm', idealMin: 200, idealMax: 400 },
        stabilizer: { min: 0, max: 300, step: 5, unit: 'ppm', idealMin: 30, idealMax: 50 }
    };

    const labels = {
        ph: 'pH',
        chlorine: 'Chlore',
        calcium: 'Calcaire (TH)',
        stabilizer: 'Stabilisant'
    };

    const handleAnalyze = async () => {
        setLoading(true);
        try {
            const data = await apiRequest('/analysis', {
                method: 'POST',
                body: JSON.stringify(values)
            });
            setResults(data);
            toast.success('Analyse terminée !');
        } catch (error) {
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (productId) => {
        if (!user) {
            toast.error('Connectez-vous pour ajouter au panier');
            navigate('/auth');
            return;
        }
        try {
            await addToCart(productId);
            toast.success('Produit ajouté au panier');
        } catch (error) {
            toast.error(error.message);
        }
    };

    const getGaugePosition = (param, value) => {
        const range = ranges[param];
        return ((value - range.min) / (range.max - range.min)) * 100;
    };

    const getGaugeColor = (status) => {
        switch (status) {
            case 'ideal': return 'bg-emerald-500';
            case 'low':
            case 'high': return 'bg-amber-500';
            default: return 'bg-red-500';
        }
    };

    return (
        <div className="min-h-screen pt-20 pb-12" data-testid="analysis-page">
            {/* SEO for Analysis Page */}
            <SEOHead 
                title={PAGE_SEO.analysis.title}
                description={PAGE_SEO.analysis.description}
                keywords={PAGE_SEO.analysis.keywords}
                canonicalUrl="https://www.ocapiscine.fr/analysis"
            />
            
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <FlaskConical className="h-10 w-10 text-primary" />
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold">Analyse d'eau</h1>
                    </div>
                    <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-6">
                        Entrez les valeurs de vos bandelettes de test pour recevoir des recommandations personnalisées.
                    </p>
                    
                    {/* Photo analysis CTA */}
                    <Link to="/analysis/photo" data-testid="photo-analysis-link">
                        <Button variant="secondary" className="rounded-full">
                            <Camera className="h-4 w-4 mr-2" />
                            Analyser par photo de bandelette
                        </Button>
                    </Link>
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    {/* Input Form */}
                    <Card className="glass rounded-3xl" data-testid="analysis-form">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Droplets className="h-5 w-5 text-primary" />
                                Paramètres de l'eau
                            </CardTitle>
                            <CardDescription>
                                Ajustez les curseurs selon vos mesures
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-8">
                            {Object.entries(ranges).map(([param, range]) => (
                                <div key={param} className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-medium">{labels[param]}</Label>
                                        <span className="font-mono text-lg font-semibold text-primary">
                                            {values[param]}{range.unit}
                                        </span>
                                    </div>
                                    
                                    {/* Gauge visualization */}
                                    <div className="relative h-3 bg-muted rounded-full overflow-hidden">
                                        {/* Ideal zone indicator */}
                                        <div 
                                            className="absolute h-full bg-emerald-500/30"
                                            style={{
                                                left: `${(range.idealMin / range.max) * 100}%`,
                                                width: `${((range.idealMax - range.idealMin) / range.max) * 100}%`
                                            }}
                                        />
                                        {/* Current value indicator */}
                                        <div 
                                            className="absolute h-full w-1 bg-primary rounded-full transition-all duration-300"
                                            style={{ left: `${getGaugePosition(param, values[param])}%` }}
                                        />
                                    </div>
                                    
                                    <Slider
                                        value={[values[param]]}
                                        min={range.min}
                                        max={range.max}
                                        step={range.step}
                                        onValueChange={([value]) => setValues(prev => ({ ...prev, [param]: value }))}
                                        className="mt-2"
                                        data-testid={`slider-${param}`}
                                    />
                                    
                                    <div className="flex justify-between text-xs text-muted-foreground">
                                        <span>{range.min}{range.unit}</span>
                                        <span className="text-emerald-500">Zone idéale: {range.idealMin}-{range.idealMax}{range.unit}</span>
                                        <span>{range.max}{range.unit}</span>
                                    </div>
                                </div>
                            ))}

                            {/* Pool volume */}
                            <div className="space-y-2">
                                <Label htmlFor="pool_volume" className="text-base font-medium">
                                    Volume du bassin (litres)
                                </Label>
                                <Input
                                    id="pool_volume"
                                    type="number"
                                    value={values.pool_volume}
                                    onChange={(e) => setValues(prev => ({ ...prev, pool_volume: Number(e.target.value) }))}
                                    className="h-12 rounded-xl font-mono"
                                    data-testid="input-volume"
                                />
                                <p className="text-xs text-muted-foreground">
                                    50 000 L = piscine 10m x 5m x 1m de profondeur moyenne
                                </p>
                            </div>

                            <Button 
                                onClick={handleAnalyze} 
                                className="w-full rounded-full h-12 font-semibold"
                                disabled={loading}
                                data-testid="analyze-btn"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                        Analyse en cours...
                                    </>
                                ) : (
                                    'Analyser mon eau'
                                )}
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Results */}
                    <div className="space-y-6">
                        {results ? (
                            <>
                                {/* Overall Status */}
                                <Card className={`glass rounded-3xl border-2 ${
                                    results.results.overall === 'good' ? 'border-emerald-500/50' :
                                    results.results.overall === 'warning' ? 'border-amber-500/50' :
                                    'border-red-500/50'
                                }`} data-testid="results-card">
                                    <CardHeader>
                                        <div className="flex items-center justify-between">
                                            <CardTitle className="flex items-center gap-2">
                                                {results.results.overall === 'good' ? (
                                                    <CheckCircle className="h-6 w-6 text-emerald-500" />
                                                ) : results.results.overall === 'warning' ? (
                                                    <AlertTriangle className="h-6 w-6 text-amber-500" />
                                                ) : (
                                                    <AlertTriangle className="h-6 w-6 text-red-500" />
                                                )}
                                                Résultats de l'analyse
                                            </CardTitle>
                                            <ShareAnalysisButton analysisData={results} />
                                        </div>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        {['ph', 'chlorine', 'calcium', 'stabilizer'].map(param => {
                                            const result = results.results[param];
                                            return (
                                                <div key={param} className="flex items-center justify-between p-3 rounded-xl bg-muted/50">
                                                    <div>
                                                        <span className="font-medium">{labels[param]}</span>
                                                        <span className="font-mono ml-2">{result.value}{ranges[param].unit}</span>
                                                    </div>
                                                    <Badge className={`${getStatusColor(result.status)} border-0`}>
                                                        {getStatusLabel(result.status)}
                                                    </Badge>
                                                </div>
                                            );
                                        })}
                                    </CardContent>
                                </Card>

                                {/* Recommendations */}
                                {results.recommendations.length > 0 ? (
                                    <Card className="glass rounded-3xl" data-testid="recommendations-card">
                                        <CardHeader>
                                            <CardTitle className="flex items-center gap-2">
                                                <Info className="h-5 w-5 text-primary" />
                                                Recommandations
                                            </CardTitle>
                                            <CardDescription>
                                                Produits CTX Pro conseillés pour équilibrer votre eau
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent className="space-y-4">
                                            {results.recommendations.map((rec, index) => (
                                                <div 
                                                    key={index} 
                                                    className={`p-4 rounded-2xl border ${
                                                        rec.priority === 'high' ? 'border-red-500/30 bg-red-500/5' :
                                                        rec.priority === 'medium' ? 'border-amber-500/30 bg-amber-500/5' :
                                                        'border-border bg-muted/30'
                                                    }`}
                                                    data-testid={`recommendation-${index}`}
                                                >
                                                    <div className="flex items-start justify-between gap-4">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h4 className="font-semibold">{rec.product_name}</h4>
                                                                <Badge variant={rec.priority === 'high' ? 'destructive' : 'secondary'} className="text-xs">
                                                                    {rec.priority === 'high' ? 'Urgent' : rec.priority === 'medium' ? 'Recommandé' : 'Optionnel'}
                                                                </Badge>
                                                            </div>
                                                            <p className="text-sm text-muted-foreground mb-2">{rec.reason}</p>
                                                            <p className="text-sm font-medium font-mono bg-primary/10 text-primary px-3 py-1 rounded-full inline-block">
                                                                Dosage: {rec.dosage}
                                                            </p>
                                                        </div>
                                                        {rec.product_id && (
                                                            <Button 
                                                                size="sm" 
                                                                className="rounded-full"
                                                                onClick={() => handleAddToCart(rec.product_id)}
                                                                data-testid={`add-${rec.product_id}`}
                                                            >
                                                                <ShoppingCart className="h-4 w-4 mr-1" />
                                                                Ajouter
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </CardContent>
                                    </Card>
                                ) : (
                                    <Card className="glass rounded-3xl border-2 border-emerald-500/50">
                                        <CardContent className="py-8 text-center">
                                            <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
                                            <h3 className="font-heading text-xl font-semibold mb-2">Eau parfaitement équilibrée !</h3>
                                            <p className="text-muted-foreground">
                                                Tous vos paramètres sont dans les zones idéales. Continuez ainsi !
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Link to shop */}
                                <div className="text-center">
                                    <Link to="/shop" data-testid="shop-link">
                                        <Button variant="secondary" className="rounded-full">
                                            Voir tous les produits CTX Pro
                                        </Button>
                                    </Link>
                                </div>
                            </>
                        ) : (
                            <Card className="glass rounded-3xl">
                                <CardContent className="py-16 text-center">
                                    <Droplets className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                                    <h3 className="font-heading text-xl font-semibold mb-2 text-muted-foreground">
                                        Analysez votre eau
                                    </h3>
                                    <p className="text-muted-foreground">
                                        Ajustez les paramètres et cliquez sur "Analyser" pour voir les résultats.
                                    </p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
